import React from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Eyebrow } from './brand';
import faqFuncionalidades from '../../content/faq/funcionalidades.json';

/**
 * Bloques SEO de /funcionalidades (keyword principal: "app para registro de
 * ganado"). Texto del documento "Optimización de contenido: Funcionalidades de
 * MiCampoDigital" (septiembre 2026), con los [CONFIRMAR] resueltos: roles por
 * persona (docs/ROLES_PERMISOS.md), TXT para el SIGSA, todo gratis hoy, sin app
 * de iPhone por ahora. Solo se SUMA texto; voseo; sin guiones largos.
 */

const FUENTE_SENASA = 'https://www.argentina.gob.ar/senasa/sistema-de-identificacion-electronica-de-animales';
const FUENTE_841 =
  'https://cvpba.org/identificacion-electronica-obligatoria-puntos-clave-de-la-resolucion-841-20255/';
const FUENTE_CREA = 'https://www.contenidoscrea.org.ar/ganaderia/guia-elegir-una-app-ganadera-n5326468';

/** B1 · Definición (AI Overview). Después del subtítulo del H1, antes de las tres tarjetas. */
export const BloqueRegistroGanado: React.FC = () => (
  <div className="lp-prosa" style={{ marginTop: 40 }}>
    <h2 className="lp-h3">Una app para llevar el registro de tu ganado, animal por animal</h2>
    <p>
      Una app para registro de ganado guarda en el celular todo lo que le pasa a cada animal del rodeo, identificado por
      su caravana: alta, pesadas, tactos, vacunas, movimientos entre potreros y baja. Con esos registros arma sola los
      indicadores del campo, como ganancia diaria, tasa de preñez o kilos producidos, sin pasar datos a una planilla.
    </p>
    <p>
      MiCampoDigital hace ese registro a nivel individual, no solo por lote. Si el animal tiene caravana electrónica, el
      lector la carga y la balanza pone el peso; si no la tiene todavía, lo cargás por el número de la caravana visual.
      Abajo está cada funcionalidad en detalle, en el orden en que la usa un productor en la manga.
    </p>
  </div>
);

/** B2 · Segundo párrafo de "Funciona sin señal". */
export const ExtraSinSenal: React.FC = () => (
  <div className="lp-sec-extra">
    <p>
      Cuando el teléfono vuelve a tener conexión, en la casa, en la ruta o en el pueblo, los datos se sincronizan solos
      con la cuenta del establecimiento. No hay que apretar nada ni acordarse de exportar. Lo mismo pasa con las notas de
      voz al asistente: se graban sin señal y se envían al reconectar.
    </p>
  </div>
);

/** B3 · Qué exige el SENASA (H3). Dentro de "Caravana electrónica", después de la lista. */
export const ExtraSenasa: React.FC = () => (
  <div className="lp-senasa">
    <h3>Qué exige el SENASA desde 2026</h3>
    <p>
      La{' '}
      <a href={FUENTE_841} target="_blank" rel="noopener noreferrer">
        Resolución 841/2025 del SENASA
      </a>{' '}
      hizo obligatoria la identificación electrónica: desde el 1 de enero de 2026, ningún ternero bovino o bubalino
      puede moverse ni quedarse en su campo de nacimiento después del destete sin su dispositivo electrónico oficial. El{' '}
      <a href={FUENTE_SENASA} target="_blank" rel="noopener noreferrer">
        SENASA acepta
      </a>{' '}
      botón RFID de baja frecuencia o transponder inyectable en la oreja derecha, o bolo ruminal, siempre junto con la
      caravana visual en la oreja izquierda. La colocación se declara dentro de los 10 días hábiles.
    </p>
    <p>
      La app no hace la declaración por vos, pero te la deja lista: arma el archivo TXT con las caravanas para subir al
      portal del SIGSA y, cuando confirmás que lo subiste, te marca qué animales ya declaraste y cuáles faltan. Después,
      cada número electrónico queda asociado a su animal con toda la historia, y la próxima vez que pasa por la manga el
      lector lo reconoce en un segundo.
    </p>
    <p>
      Paso a paso:{' '}
      <Link to="/blog/caravana-electronica-senasa-obligatoria">
        caravana electrónica del SENASA, qué exige y cómo declararla
      </Link>
      .
    </p>
  </div>
);

/** Link interno a la guía de preñez (sección "Seguimiento reproductivo"). */
export const ExtraPrenez: React.FC = () => (
  <p className="lp-sec-extra" style={{ marginTop: 18 }}>
    Paso a paso: <Link to="/blog/control-de-prenez-paso-a-paso">control de preñez paso a paso</Link>.
  </p>
);

/** Link interno a precios (sección "Precios y valuación"). */
export const ExtraPrecios: React.FC = () => (
  <p className="lp-sec-extra" style={{ marginTop: 18 }}>
    Mirá el <Link to="/precios">precio de la hacienda en Cañuelas</Link>, gratis y sin cuenta.
  </p>
);

/** B4 · Segundo párrafo de "Mapa del campo". */
export const ExtraMapa: React.FC = () => (
  <div className="lp-sec-extra">
    <p>
      La app mide el verde de cada cuadro mes a mes con imágenes satelitales y estima cuántas raciones aguanta. Así ves
      qué potreros están cerca de su límite de carga antes de que el pasto se note corto, y planificás la rotación con un
      dato en la mano.
    </p>
  </div>
);

const ROLES = [
  { rol: 'Dueño', desc: 've y maneja todo, incluidas las personas que entran al campo.' },
  { rol: 'Administrador', desc: 'casi todo lo del dueño.' },
  { rol: 'Gerente', desc: 'carga y corrige ganado y lotes, y mira los reportes.' },
  { rol: 'Operador', desc: 'carga el trabajo del día en la manga, sin ver gastos ni administración.' },
  { rol: 'Solo lectura', desc: 'mira todo sin modificar nada, por ejemplo el asesor.' },
];

/** B5 · Trabajo en equipo: segundo párrafo + roles (confirmados). */
export const ExtraEquipo: React.FC = () => (
  <div className="lp-sec-extra">
    <p>
      Se terminan las planillas que viajan por WhatsApp con versiones distintas y la pregunta de cuál es la última. Hay
      un solo rodeo, con una sola historia por animal.
    </p>
    <p>Cada persona entra con su rol, así cada uno ve y carga lo que le toca:</p>
    <ul>
      {ROLES.map((r) => (
        <li key={r.rol}>
          <strong>{r.rol}:</strong> {r.desc}
        </li>
      ))}
    </ul>
    <p>
      ¿Querés ver cómo se organizan otros campos? Pasá por el <Link to="/foro">foro de productores</Link>.
    </p>
  </div>
);

const MAPA_FUNCIONES: { funcion: string; que: React.ReactNode; nivel: string }[] = [
  { funcion: 'Gestión del rodeo', que: 'Alta, pesadas, tactos, vacunas y movimientos por caravana', nivel: 'Individual y por lote' },
  { funcion: 'Gestión económica', que: 'Gastos, compras, inventario, libro diario y resultados', nivel: 'Por establecimiento' },
  {
    funcion: 'Corrales y engorde',
    que: (
      <>
        <Link to="/calculadora-racion">Calculadora de ración</Link> y{' '}
        <Link to="/calculadora-engorde">calculadora de engorde</Link> con costo por kilo ganado
      </>
    ),
    nivel: 'Por lote',
  },
  { funcion: 'Forraje', que: 'Verde por potrero y capacidad de carga desde el satélite', nivel: 'Por potrero' },
  { funcion: 'Indicadores', que: 'Preñez, destete, kilos producidos y evolución de peso', nivel: 'Individual y por categoría' },
  {
    funcion: 'Integración',
    que: 'Lector EID/RFID, balanza, importación de Excel o CSV, exportación de datos y archivo para el SIGSA',
    nivel: 'Por animal',
  },
];

/** B6 · Tabla resumen con las categorías de CREA. Antes de "Gratis y sin tarjeta". */
export const TablaFuncionalidades: React.FC = () => (
  <section id="mapa-de-funcionalidades" className="lp-sec lp-sec--white">
    <div className="lp-wrap">
      <div className="lp-sec-h" style={{ maxWidth: 760 }}>
        <Eyebrow>Resumen</Eyebrow>
        <h2 className="lp-h2">Mapa de funcionalidades de MiCampoDigital</h2>
        <p className="lp-lead">
          La{' '}
          <a className="lp-inline" href={FUENTE_CREA} target="_blank" rel="noopener noreferrer">
            guía de CREA para elegir una app ganadera
          </a>{' '}
          compara las apps por seis funciones: gestión del rodeo, gestión económica, corrales, forraje, indicadores e
          integración con otras herramientas. Esta es la foto de MiCampoDigital en cada una:
        </p>
      </div>
      <div className="lp-tabla-wrap">
        <table className="lp-tabla">
          <thead>
            <tr>
              <th scope="col">Función</th>
              <th scope="col">Qué hace MiCampoDigital</th>
              <th scope="col">Nivel</th>
            </tr>
          </thead>
          <tbody>
            {MAPA_FUNCIONES.map((r) => (
              <tr key={r.funcion}>
                <th scope="row">{r.funcion}</th>
                <td>{r.que}</td>
                <td>{r.nivel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

/** FAQ visible (8 preguntas). Coincide con el FAQPage del prerender (mismo JSON). */
export const FaqFuncionalidades: React.FC = () => (
  <section id="preguntas" className="lp-faq lp-sec">
    <div className="lp-wrap">
      <div className="lp-split">
        <div className="lp-sec-h">
          <Eyebrow>Preguntas</Eyebrow>
          <h2 className="lp-h2">Preguntas frecuentes sobre la app.</h2>
          <p className="lp-lead">
            ¿Tenés todo en Excel? Mirá cómo <Link className="lp-inline" to="/#excel">importar tu planilla de Excel</Link>.
          </p>
        </div>
        <div>
          {faqFuncionalidades.preguntas.map((p, i) => (
            <details key={p.q} open={i === 0}>
              <summary>
                {p.q}
                <Plus aria-hidden="true" />
              </summary>
              <p>{p.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  </section>
);
